// Copyright 2016 Sorama B.V.

import { ExponentialSmoother } from "algorithms/ExponentialSmoother";
import { autoinject, BindingEngine, computedFrom, Disposable, observable, transient } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { DataOrder, Grid, GridData } from "controls/plot/grid-plot/GridData";
import {
    Point3D,
    Region,
    Resolution,
    StreamBeamformingProjection,
    StreamBeamformingProjectionProperties,
} from "datatypes/SoramaLocalApi";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { userSetting } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { SettingsModalState } from "modules/modals/SettingsModal";
import { IColorMap } from "services/ColorMap/IColorMap";
import { IColorMapService } from "services/ColorMap/IColorMapService";
import { FrequencySelectionService } from "services/FrequencySelection/FrequencySelectionService";
import { IMeasurementPreviewService } from "services/Measurement/IMeasurementPreviewService";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { Length } from "units/Length";
import { Pressure } from "units/Pressure";
import { Unit } from "units/Unit";

/**
 * MODULE OPERATION DESCRIPTION - FarFieldModule
 * The far field module is a display module that continuously displays frequency domain beamforming projections.
 * The stream is active when the 'isStreamMeasurementActive' properties on both the VideoStreamPreviewService and the SoundStreamPreviewService are true.
 * Frequency selection for the beamforming projections are provided by the workflowproperty named 'selectedFrequencies'
 */
@transient()
@autoinject
export class FarFieldModule implements IWorkflowModuleLifetime {
    //#region User Settings Storage Keys
    private static dataColorMapSettingKey = "FarFieldModuleDataColorMap";
    //#endregion

    private static maximumResolutionDimension = 48;

    /** Minimum and maximum time constant (s) for the logarithmic smoothing slider. */
    private smoothingMin = 0.01;
    private smoothingMax = 5;

    private videoResolution: Resolution = { width: 1280, height: 720 };
    private aspectRatio: number = 16 / 9;
    private resolution: Resolution = { width: 32, height: 18 };

    private bindPromise: Promise<void>;
    private streamPromise: Promise<unknown>;

    private observerSubscriptions: Disposable[];
    private targetPoints: Point3D[];
    private targetGrid: Grid;
    private cancelStream: boolean;
    private _dataColorMap: IColorMap;

    // Module properties
    @userSetting() public opacity = 60;
    @userSetting() public threshold = 30;
    @observable @userSetting() public smoothingTime = 0;

    /** Maps the stored s value to a 0–1 slider position on a logarithmic scale. */
    @computedFrom("smoothingTime")
    public get smoothingSlider(): number {
        if (this.smoothingTime <= 0) return 0;
        return (
            Math.round(
                (Math.log(this.smoothingTime / this.smoothingMin) / Math.log(this.smoothingMax / this.smoothingMin)) *
                    100
            ) / 100
        );
    }
    public set smoothingSlider(position: number) {
        this.smoothingTime =
            position <= 0
                ? 0
                : Math.round(this.smoothingMin * Math.pow(this.smoothingMax / this.smoothingMin, position) * 1000) /
                  1000;
    }

    @observable
    public distance = 10;

    public videoImage: string;

    public data: GridData;
    public dataDisplayUnit: Unit = Pressure.pascal;
    moduleInstance: IWorkflowModuleInstance;
    private streamerFarFieldSettingsModal: IWorkflowModuleModalInstance<SettingsModalState<FarFieldModule>, void>;

    @computedFrom("_dataColorMap")
    public get dataColorMap(): IColorMap {
        return this._dataColorMap;
    }
    public set dataColorMap(value: IColorMap) {
        if (!this._dataColorMap) {
            // Wait until the colorMap service has defined this property, otherwise the saved value is overwritten
            return;
        }

        this._dataColorMap = value;
        this.colorMapService.setDefaultColorMap(FarFieldModule.dataColorMapSettingKey, value);
    }

    constructor(
        private readonly previewService: IMeasurementPreviewService,
        private readonly colorMapService: IColorMapService,
        private readonly bindingEngine: BindingEngine,
        private readonly frequencySelectionService: FrequencySelectionService,
        private readonly smoother: ExponentialSmoother
    ) {}

    //#region Lifecycle functions
    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        const soundObserver = this.bindingEngine.propertyObserver(
            this.previewService,
            "isBeamformingProjectionAvailable"
        );

        const videoObserver = this.bindingEngine.propertyObserver(this.previewService, "isVideoAvailable");

        this.observerSubscriptions = [
            soundObserver.subscribe(this.onBeamformingProjectionStatusChanged),
            videoObserver.subscribe(this.onVideoStatusChanged),
        ];

        // Set some default grid with some zeros to make the plot scale to 16:9
        this.clearGrid();

        this.onBeamformingProjectionStatusChanged();
        this.onVideoStatusChanged();

        // Bind all user settings
        const promises = [
            this.colorMapService.getDefaultColorMap(FarFieldModule.dataColorMapSettingKey).then((colorMap) => {
                this._dataColorMap = colorMap;
            }),
        ];

        this.bindPromise = Promise.all(promises)
            .catch(() => {})
            .then(() => {});
    }

    public deactivateModule(): void {
        this.bindPromise.then(() => {
            while (this.observerSubscriptions.length > 0) this.observerSubscriptions.pop().dispose();

            this.videoImage = null;
        });
    }

    //#endregion

    //#region Stream functions

    /**
     * Beamforming projection status changed callback
     */
    private onBeamformingProjectionStatusChanged = () => {
        const stopPromise = this.stopStream();

        if (
            this.previewService.isBeamformingProjectionAvailable.available &&
            this.previewService.isVideoAvailable.available
        ) {
            stopPromise.then(() => this.startStream());
        } else if (
            !this.previewService.isBeamformingProjectionAvailable.available &&
            this.previewService.isBeamformingProjectionAvailable.reason
        ) {
            this.moduleInstance
                .createNotification()
                .withUrgency(
                    this.previewService.isBeamformingProjectionAvailable.inProgress
                        ? NotificationUrgency.Info
                        : NotificationUrgency.Warning
                )
                .withMessage(this.previewService.isBeamformingProjectionAvailable.reason)
                .show();
        }
    };

    /**
     * Video status changed callback
     */
    private onVideoStatusChanged = () => {
        const stopPromise = this.stopStream();

        if (
            this.previewService.isBeamformingProjectionAvailable.available &&
            this.previewService.isVideoAvailable.available
        ) {
            stopPromise.then(() => this.startStream());
        } else if (
            this.previewService.isBeamformingProjectionAvailable.available &&
            !this.previewService.isVideoAvailable.available
        ) {
            this.moduleInstance
                .createNotification()
                .withUrgency(
                    this.previewService.isVideoAvailable.inProgress
                        ? NotificationUrgency.Info
                        : NotificationUrgency.Warning
                )
                .withMessage(this.previewService.isVideoAvailable.reason)
                .show();
        }
    };

    private distanceChanged(): void {
        if (this.previewService && this.previewService.isVideoAvailable.available) this.computeMeshGrid();
    }

    /**
     * Starts the complete streaming chain
     */
    private startStream(): void {
        if (this.streamPromise) return;

        // First initialise video stream, compute beamforming targets, and start the sound stream
        this.streamPromise = this.previewService
            .getResolution()
            .then((resolution) => {
                // Compute new values
                this.videoResolution = resolution;
                this.aspectRatio = this.videoResolution.width / this.videoResolution.height;
                return this.computeMeshGrid().then(() => {
                    const emptyData = <number[]>new Array(this.resolution.width * this.resolution.height);
                    for (let i = 0; i < this.resolution.width * this.resolution.height; i++) {
                        emptyData[i] = 1;
                    }

                    // Set some dummy data to the grid plot to make it resize to the correct size
                    this.data = new GridData(
                        emptyData,
                        Pressure.pascal,
                        DataOrder.LeftToRightThenTopToBottom,
                        this.targetGrid
                    );

                    // Get video stream
                    return this.previewService.getStreamAddress().then((address) => {
                        this.videoImage = address;
                        return this.beginGetProjection();
                    });
                });
            })
            .catch(() => {
                this.streamPromise = null;
            });
    }

    /**
     * Stops the streaming chain, when the returned promise resolves the stream is garantueed to be stopped
     */
    private stopStream(): Promise<void> {
        // Signal to cancel
        this.cancelStream = true;

        // Wait for the steam promise to finish and reset all state to default
        const promise = this.streamPromise ? this.streamPromise : Promise.resolve();

        return promise
            .catch(() => {})
            .then(() => {
                this.videoImage = null;
                this.streamPromise = null;
                this.smoother.reset();
            });
    }

    /**
     * Starts a request for a new projection
     */
    private beginGetProjection(): Promise<void> {
        const properties = <StreamBeamformingProjectionProperties>{
            frequencies: this.frequencySelectionService.selectedRanges,
            targetPoints: this.targetPoints,
        };
        const grid = this.targetGrid;

        this.cancelStream = false;
        return this.previewService
            .previewBeamformingProjection(properties)
            .then((projection: StreamBeamformingProjection) => this.endGetProjection(projection, grid))
            .catch((e) => {
                this.clearGrid();
            });
    }

    /**
     * Called when a request for a projection has been completed
     * @param projection The requested projection
     * @param grid The grid used in the request
     */
    private endGetProjection(projection: StreamBeamformingProjection, grid: Grid): Promise<void> | void {
        this.smoother.timeConstant = this.smoothingTime;
        const displayData = this.smoother.smooth(projection.data, performance.now() / 1000);

        // Create a grid data object
        this.data = new GridData(displayData, Pressure.pascal, DataOrder.LeftToRightThenTopToBottom, grid);

        // Request new data if needed
        if (!this.cancelStream) return this.beginGetProjection();

        this.clearGrid();
    }

    //#endregion

    //#region Helper functions

    private clearGrid(): void {
        const grid = new Grid({ x: 16, y: 9 }, { x: 1, y: 1 }, { x: 0, y: 0 }, Length.meter);

        const dummyData = <number[]>new Array(16 * 9);
        for (let i = 0; i < 16 * 9; i++) {
            dummyData[i] = 0;
        }
        this.data = new GridData(dummyData, Pressure.pascal, DataOrder.LeftToRightThenTopToBottom, grid);
    }

    private computeMeshGrid(): Promise<void> {
        // Request visible region
        return this.previewService.getVisibleRegion(this.distance).then((region: Region) => {
            // Determine beamforming resolution
            const scale = Math.min(
                FarFieldModule.maximumResolutionDimension / this.videoResolution.width,
                FarFieldModule.maximumResolutionDimension / this.videoResolution.height
            );

            this.resolution = {
                width: Math.round(scale * this.videoResolution.width),
                height: Math.round(scale * this.videoResolution.height),
            };

            const targetPoints = <Point3D[]>[];

            const resX = this.resolution.width;
            const resY = this.resolution.height;

            const width = region.width;
            const height = region.height;

            for (let y = 0; y < resY; y++) {
                for (let x = 0; x < resX; x++) {
                    targetPoints.push({
                        x: ((x + 0.5) / resX) * width - width * 0.5 + region.centerX,
                        y: ((y + 0.5) / resY) * height - height * 0.5 + region.centerY,
                        z: this.distance,
                    });
                }
            }

            this.targetPoints = targetPoints;
            this.targetGrid = new Grid(
                { x: resX, y: resY },
                { x: width / resX, y: height / resY },
                { x: 0, y: 0 },
                Length.meter
            );
        });
    }

    public openStreamerFarFieldSettingsModal(): void {
        if (this.streamerFarFieldSettingsModal && this.streamerFarFieldSettingsModal.isOpen) {
            this.streamerFarFieldSettingsModal.cancel();
            this.streamerFarFieldSettingsModal = undefined;
            return;
        }
        this.moduleInstance
            .createModal<SettingsModalState<FarFieldModule>, void>(WorkflowModalCatalog.StreamerFarFieldSettingsModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.streamerFarFieldSettingsModal = undefined;
            })
            .show()
            .then((x) => {
                this.streamerFarFieldSettingsModal = x;
            });
    }

    //#endregion
}
